/**
 * Drag and drop interactions for the dashboard editor canvas.
 */
const dashboardLayoutEditorCanvasDrag = {
    /**
     * Start dashboard widget drag.
     *
     * @param {*} event
     * @param {*} block
     */
    startDashboardWidgetDrag(event, block) {
        if (this.dashboardCanvasResizeState) {
            event?.preventDefault();
            return;
        }

        const pointerPosition = this.getDashboardPointerPosition(event);
        const columnSpan = Math.max(Number(block.columnSpan || 1), 1);
        const rowSpan = Math.max(Number(block.rowSpan || 1), 1);
        // Remember the cell under the pointer within the block. Without this,
        // every drag-over would treat the pointer cell as the block's top-left
        // corner and make a multi-cell block jump as soon as dragging starts.
        const dragAnchorColumn = Math.min(
            Math.max(pointerPosition.column - block.column, 0),
            columnSpan - 1,
        );
        const dragAnchorRow = Math.min(
            Math.max(pointerPosition.row - block.row, 0),
            rowSpan - 1,
        );

        if (event?.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.dropEffect = 'move';
            event.dataTransfer.setData('text/plain', block.referenceId);
        }

        this.dashboardCanvasCommittedReferenceId = null;
        this.dashboardCanvasDragReferenceId = block.referenceId;
        this.activeBlock = {
            referenceId: block.referenceId,
            columnSpan,
            rowSpan,
            dragAnchorColumn,
            dragAnchorRow,
        };
    },

    /**
     * Start dashboard block drag.
     *
     * @param {*} event
     * @param {*} block
     */
    startDashboardBlockDrag(event, block) {
        this.startDashboardWidgetDrag(event, block);
    },

    /**
     * Stop dashboard widget drag.
     */
    stopDashboardWidgetDrag() {
        this.dashboardCanvasDragReferenceId = null;
        this.dashboardCanvasDropTarget = null;
        this.activeBlock = null;
    },

    /**
     * Stop dashboard block drag.
     */
    stopDashboardBlockDrag() {
        this.stopDashboardWidgetDrag();
    },

    /**
     * Handle dashboard canvas drag over.
     *
     * @param {*} event
     */
    handleDashboardCanvasDragOver(event) {
        if (!this.activeBlock) {
            return;
        }

        const pointerPosition = this.getDashboardPointerPosition(event);
        // Sidebar widgets do not originate from a canvas block, so they have
        // no grab offset yet. Treat their top-left cell as the grab point.
        const dragAnchorColumn = Math.max(Number(this.activeBlock.dragAnchorColumn ?? 0), 0);
        const dragAnchorRow = Math.max(Number(this.activeBlock.dragAnchorRow ?? 0), 0);
        // Retain the original grab point by translating the pointer position
        // back to the block's top-left candidate before finding free space.
        const target = this.findDashboardDropTarget(
            Math.max(pointerPosition.column - dragAnchorColumn, 1),
            Math.max(pointerPosition.row - dragAnchorRow, 1),
            this.activeBlock,
            this.activeBlock.referenceId,
        );

        this.dashboardCanvasDropTarget = target;
    },

    /**
     * Handle dashboard canvas drop.
     *
     * Recalculate the target from the drop event as a final safeguard. A
     * native drag can occasionally skip the final dragover event, which used
     * to leave the target empty and caused the sidebar draft to be discarded
     * during dragend.
     *
     * @param {DragEvent} event
     */
    handleDashboardCanvasDrop(event) {
        if (!this.activeBlock) {
            return;
        }

        const pointerPosition = this.getDashboardPointerPosition(event);
        const dragAnchorColumn = Math.max(Number(this.activeBlock.dragAnchorColumn ?? 0), 0);
        const dragAnchorRow = Math.max(Number(this.activeBlock.dragAnchorRow ?? 0), 0);
        const pointerDropTarget = this.findDashboardDropTarget(
            Math.max(pointerPosition.column - dragAnchorColumn, 1),
            Math.max(pointerPosition.row - dragAnchorRow, 1),
            this.activeBlock,
            this.activeBlock.referenceId,
        );
        const currentPosition = this.getBreakpointPositioningEntry(this.activeBlock.referenceId);
        const currentPositionDropTarget = currentPosition
            ? this.findDashboardDropTarget(
                Math.max(Number(currentPosition.offset ?? 0) + 1, 1),
                Math.max(Number(currentPosition.row ?? 1), 1),
                this.activeBlock,
                this.activeBlock.referenceId,
            )
            : null;
        const dropTarget = pointerDropTarget
            || this.dashboardCanvasDropTarget
            || currentPositionDropTarget;

        if (!dropTarget) {
            return;
        }

        this.setDashboardWidgetPosition(this.activeBlock.referenceId, dropTarget);
        this.dashboardCanvasCommittedReferenceId = this.activeBlock.referenceId;
        this.stopDashboardWidgetDrag();
    },
};

export default dashboardLayoutEditorCanvasDrag;
